import {FlyoutDescription, FlyoutHeading, FlyoutItem, FlyoutLabel} from "catalyst";
import type {MouseEvent, ReactNode} from "react";

export type OtsNavLink = {
  id: string;
  label: string;
  /** A heroicon, sized by the bar or the flyout. */
  icon?: ReactNode;
  /** One line under the label in a flyout. */
  description?: string;
  /** A count beside the item, e.g. work waiting; hidden at zero. */
  badgeCount?: number;
  disabled?: boolean;
  onClick: () => void;
};

export type OtsNavFlyoutColumn = {
  /** Left out when the flyout has a single column named by the item that opened it. */
  title?: string;
  items: OtsNavLink[];
};

export type OtsNavFlyoutMenu = {
  columns: OtsNavFlyoutColumn[];
  /** Shortcuts in a shaded row under the columns. */
  actions?: OtsNavLink[];
  footer?: {
    title?: string;
    /** `fullWidth` stacks the items; `columns` (the default) sets them side by side. */
    layout?: "columns" | "fullWidth";
    items: OtsNavLink[];
  };
};

export const OtsNavCount = ({count, className}: {count?: number; className?: string}) => count && count > 0
  ? <span className={["inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-[var(--ots-accent,#E1251B)] px-1.5 text-[0.65rem]/5 font-semibold text-white", className].filter(Boolean).join(" ")}>
    {count > 99 ? "99+" : count}
  </span>
  : null;

/** Every destination a flyout holds, columns first, in the order it shows them. */
export const flyoutLinks = (menu: OtsNavFlyoutMenu): OtsNavLink[] => [
  ...menu.columns.flatMap(column => column.items),
  ...(menu.actions ?? []),
  ...(menu.footer?.items ?? []),
];

const focus = [
  "data-focus:!bg-[var(--ots-primary,#2563eb)] data-focus:!text-[var(--ots-on-primary,#fff)]",
  "data-focus:*:data-[slot=icon]:!text-[var(--ots-on-primary,#fff)]",
].join(" ");

/** Pinning destinations to the status strip; see useOtsNavPins. */
export type OtsNavFlyoutPins = {
  isPinned: (id: string) => boolean;
  canPin: boolean;
  max: number;
  toggle: (id: string) => void;
};

/** A pushpin, filled once pinned. Drawn here because Heroicons has none. */
export const OtsPinIcon = ({filled, className}: {filled?: boolean; className?: string}) => <svg
  viewBox="0 0 24 24"
  aria-hidden="true"
  fill={filled ? "currentColor" : "none"}
  stroke="currentColor"
  strokeWidth={1.75}
  strokeLinejoin="round"
  strokeLinecap="round"
  className={className}>
  <path d="m14.5 3.5 6 6-2.5 1-3 3 .75 4.75-1.75 1.75-4-4-5 5M8 14 4 10l1.75-1.75L10.5 9l3-3z"/>
</svg>;

const PinButton = ({link, pins}: {link: OtsNavLink; pins: OtsNavFlyoutPins}) => {
  const pinned = pins.isPinned(link.id);
  const full = !pinned && !pins.canPin;
  const label = pinned
    ? `Unpin ${link.label} from the status bar`
    : full ? `Unpin one to pin ${link.label} (${pins.max} at most)` : `Pin ${link.label} to the status bar`;
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={pinned}
      title={label}
      disabled={full}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        pins.toggle(link.id);
      }}
      className={[
        "absolute right-1 top-1/2 grid size-6 -translate-y-1/2 place-items-center rounded-md transition",
        "hover:bg-[color-mix(in_srgb,var(--ots-text,#09090b)_8%,transparent)] focus-visible:opacity-100 focus-visible:outline-2 focus-visible:outline-[var(--ots-primary)]",
        "disabled:cursor-not-allowed disabled:opacity-0 disabled:group-hover/pin:opacity-40",
        pinned
          ? "text-[var(--ots-primary,#2563eb)] opacity-100"
          : "text-[color-mix(in_srgb,var(--ots-text,#09090b)_55%,transparent)] opacity-0 group-hover/pin:opacity-100 group-focus-within/pin:opacity-100",
      ].join(" ")}>
      <OtsPinIcon filled={pinned} className="size-3.5"/>
    </button>
  );
};

const NavLinkButton = ({link, current, close, shaded, pins}: {link: OtsNavLink; current: boolean; close: () => void; shaded?: boolean; pins?: OtsNavFlyoutPins}) => {
  const onClick = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    link.onClick();
    close();
  };

  const item = (
    <FlyoutItem
      onClick={onClick}
      disabled={link.disabled}
      aria-current={current ? "page" : undefined}
      data-current={current ? "true" : undefined}
      className={[
        "!items-center !gap-2 !px-2 !py-1.5 !text-[var(--ots-text,#09090b)]",
        pins ? "!pr-8" : "",
        shaded && !current ? "bg-[color-mix(in_srgb,var(--ots-text,#09090b)_4%,transparent)]" : "",
        current ? "!bg-[color-mix(in_srgb,var(--ots-primary,#2563eb)_12%,transparent)]" : "",
        "data-disabled:opacity-50",
        focus,
      ].join(" ")}>
      {link.icon != null && (
        <span
          aria-hidden="true"
          className={[
            "grid size-7 shrink-0 place-items-center rounded-md [&>svg]:size-4",
            current
              ? "bg-[var(--ots-primary,#2563eb)] text-[var(--ots-on-primary,#fff)]"
              : "bg-[color-mix(in_srgb,var(--ots-text,#09090b)_7%,transparent)] text-[color-mix(in_srgb,var(--ots-text,#09090b)_75%,transparent)] group-data-focus:bg-white/20 group-data-focus:text-inherit",
          ].join(" ")}>
          {link.icon}
        </span>
      )}
      <span className="min-w-0 flex-1">
        <FlyoutLabel className={["!flex items-center gap-2 text-sm/5 [font-family:var(--ots-font-display)]", current ? "!font-semibold text-[var(--ots-primary,#2563eb)] group-data-focus:text-inherit" : ""].join(" ")}>
          <span className="truncate">{link.label}</span>
          <OtsNavCount count={link.badgeCount}/>
        </FlyoutLabel>
        {link.description && <FlyoutDescription className="!mt-0 truncate !text-xs/4">{link.description}</FlyoutDescription>}
      </span>
    </FlyoutItem>
  );

  // The pin sits beside the item, not in it: the item is itself a button.
  return pins && !link.disabled
    ? <div className="group/pin relative">{item}<PinButton link={link} pins={pins}/></div>
    : item;
};

export type OtsNavFlyoutProps = {
  menu: OtsNavFlyoutMenu;
  /** Id of the destination the person is on; it is drawn in the brand color. */
  currentId?: string;
  /** Offers a pin on each destination, to keep it in the status strip. */
  pins?: OtsNavFlyoutPins;
  close: () => void;
};

/**
 * What a navbar destination opens: the dashboard's full-width flyout of titled columns, shortcuts, and a footer,
 * set compact so it reads like a menu rather than a page. Titles are Lexend over Inter descriptions, as ots-site's
 * menus set them.
 */
export const OtsNavFlyout = ({menu, currentId, pins, close}: OtsNavFlyoutProps) => {
  const columnCount = Math.min(Math.max(menu.columns.length, 1), 4);
  const actions = menu.actions ?? [];
  const footer = menu.footer;
  const rule = "mt-3 border-t border-zinc-950/10 pt-3 dark:border-white/10";

  return (
    // Centered, as the dashboard's always were; columns keep a menu's width rather than stretching across the window.
    <div className="mx-auto px-4 py-3" style={{maxWidth: `${columnCount * 17 + 2}rem`}}>
      <div className="grid gap-x-4 gap-y-3" style={{gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`}}>
        {menu.columns.map((column, index) => (
          <section key={column.title ?? index} className="min-w-0">
            {column.title && <FlyoutHeading className="!text-[11px]/4">{column.title}</FlyoutHeading>}
            <div className={["space-y-0.5", column.title ? "mt-1" : ""].join(" ")}>
              {column.items.map(link => <NavLinkButton key={link.id} link={link} current={link.id === currentId} close={close} pins={pins}/>)}
            </div>
          </section>
        ))}
      </div>
      {actions.length > 0 && (
        <div className={`${rule} grid grid-cols-1 gap-1.5 sm:grid-cols-2 lg:grid-cols-4`}>
          {actions.map(link => <NavLinkButton key={link.id} link={link} current={link.id === currentId} close={close} pins={pins} shaded/>)}
        </div>
      )}
      {footer && footer.items.length > 0 && (
        <div className={rule}>
          {footer.title && <FlyoutHeading className="!text-[11px]/4">{footer.title}</FlyoutHeading>}
          <div className={["mt-1 grid gap-1.5", footer.layout === "fullWidth" ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4"].join(" ")}>
            {footer.items.map(link => <NavLinkButton key={link.id} link={link} current={link.id === currentId} close={close} pins={pins} shaded/>)}
          </div>
        </div>
      )}
    </div>
  );
};
